import { useRouter } from 'next/router';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import { getNavigation, TNavigationItem } from '@/lib/sanity/queries';
import { Loading } from '@fagmobler/ui';
import { getArticlePageData } from '@/cataloguePages/articlePage/getData';
import { getPathFromInternalLink } from '@/lib/utils/getUrls';
import { getRedirectData, RedirectPage } from '@/cataloguePages/redirect';
import ArticlePage from '@/cataloguePages/articlePage';
import { PreviewSuspense } from '@sanity/preview-kit';
import dynamic from 'next/dynamic';

const ArticlePagePreview = dynamic(
  () => import('@/cataloguePages/articlePage/preview'),
  {
    ssr: false,
  }
);

const REDIRECT_OLD_CATEGORIES = [
  'stue',
  'soverom',
  'soverommet',
  'spiseplassen',
  'hagemobler',
  'flere-rom',
  'interior',
  'mobelpleie',
  'oppbevaring',
  'interir',
  'hagembler',
  'mbelpleie',
  'serieinfo',
];

const NOT_FOUND_SLUGS = [
  'autodiscover',
  'inspirasjon',
  'kataloger',
  'kundeservice',
  'min-butikk',
  'sitemaps',
  'utsjekk',
  'handlekurv',
  'null',
  'sdk',
];

const IGNORED_LEGACY_PATHS = [
  'bilder',
  'sok',
  'produktinfo',
  'seo',
  'ny-butikk',
  'index.php',
  'print.php',
  'ajax2.php',
  'design',
];

export interface Params extends ParsedUrlQuery {
  slugs?: string | string[];
}

const getSlugs = (params: Params | undefined): string[] => {
  return params?.slugs ? [params.slugs].flat() : [];
};

const GenericCatalogueItem = (
  props: InferGetStaticPropsType<typeof getStaticProps>
) => {
  const { commonProps = {}, data, isRedirectPage } = props;

  const router = useRouter();
  if (router.isFallback) {
    return <Loading fullscreen showBackground />;
  }
  if (isRedirectPage) {
    return <RedirectPage {...props} />;
  }

  const { params } = commonProps;

  const slugs: string[] =
    typeof params?.slugs === 'object' ? [...params.slugs] : [];

  const isArticlePage = data.transformedSanityArticlePage?._type === 'page';

  if (isArticlePage) {
    if (commonProps.preview) {
      return (
        <PreviewSuspense fallback={<Loading />}>
          <ArticlePagePreview
            {...commonProps}
            data={data}
            key={slugs.join('preview-')}
          />
        </PreviewSuspense>
      );
    }
    return <ArticlePage {...commonProps} data={data} key={slugs.join('-')} />;
  }
};

export default GenericCatalogueItem;

// Prerender all pages in main navigation
export const getStaticPaths: GetStaticPaths = async () => {
  const getPaths = (item: TNavigationItem) => {
    const paths = [];
    const path =
      item.internalLink && getPathFromInternalLink(item.internalLink, item);
    if (path) {
      paths.push(path);
    }
    if (item.children) {
      item.children.map((subitem) => {
        paths.push(getPaths(subitem).flat());
      });
    }

    return paths.flat();
  };
  const navigation = await getNavigation();
  const urls = navigation.mainNavigation
    .filter((item) => item.internalLink?.algoliaProductCategory)
    .map((item) => {
      return getPaths(item);
    })
    .flat()
    .filter(Boolean) as string[];

  const paths = urls.map((url) => {
    return {
      params: {
        slugs: url.split('/').slice(1),
      },
    };
  });
  return {
    paths: [],
    fallback: 'blocking',
  };
};

// Old rewrite rules moved from next-config.js
// rewrites() {
//   return [
//     // ...IGNORED_LEGACY_PATHS.map((path) => ({
//     //   source: `/${path}/:path*`,
//     //   destination: '/404',
//     // })),
//     {
//       source: '/xml/screens.xml',
//       destination: '/api/screens.xml',
//     },
//     {
//       source: '/xml/sitemap/sitemap.xml',
//       destination: '/sitemap.xml',
//     },
//   ];
// },

export const getStaticProps: GetStaticProps = async (context) => {
  const { preview = false, previewData = null, params } = context;
  const slugs = getSlugs(params);

  if (IGNORED_LEGACY_PATHS.includes(slugs[0])) {
    return {
      props: {},
      revalidate: 60 * 60 * 24,
      notFound: true,
    };
  }

  if (
    slugs.includes('null') ||
    slugs.includes('undefined') ||
    slugs.includes('sdk') ||
    slugs.includes('consentconfig')
  ) {
    return {
      props: {},
      revalidate: 60 * 60 * 24,
      notFound: true,
    };
  }
  const navigation = await getNavigation();

  // The nextjs documentation says
  // "Predefined routes take precedence over dynamic routes, and dynamic routes over catch all routes"
  // But we have experience that it fallbacks to this "catch all" file, if there is a 404 in the pages subfolders.
  // Because we do expensive calls to algolia to find the produkt, lets catch them here and return 404
  if (NOT_FOUND_SLUGS.includes(slugs[0])) {
    return {
      props: {},
      notFound: true,
      revalidate: 60 * 60 * 24,
    };
  }

  if (REDIRECT_OLD_CATEGORIES.includes(slugs[0])) {
    const redirectResponse = await getRedirectData({ context, slugs });
    if (redirectResponse) {
      return redirectResponse;
    } else {
      return {
        notFound: true,
        revalidate: 60 * 60 * 24,
      };
    }
  }

  const commonProps = {
    preview,
    previewData,
    navigation,
    params: context.params,
  };

  // Article Page
  const data = await getArticlePageData({ context, slugs });
  if (data) {
    return {
      props: {
        data,
        commonProps: {
          ...commonProps,
          canonical: data.canonical,
        },
        navigation,
      },
      revalidate: 60 * 60,
    };
  }

  // 404
  return {
    props: {
      commonProps,
      navigation,
    },
    revalidate: 60 * 60,
    notFound: true,
  };
};
